import { Controller } from "stimulus"
import { showLoader, hideLoader } from "../../utils/loader"
import * as Choices from 'choices.js/public/assets/scripts/choices'

export default class extends Controller {
  static targets = ["modal", "form", "ownerTypeSelect", "ownerSelectContainer", "ownerSelect", "submitBtn", "selectionInfo"]

  connect() {
    this.ownersData = []
    this.ownersLoaded = false

    if (this.hasModalTarget) {
      this.modalTarget.addEventListener('hidden.bs.modal', this.handleModalClose.bind(this))
      this.modalTarget.addEventListener('shown.bs.modal', this.handleModalOpen.bind(this))

      // Initialize Choices.js after modal is shown to ensure DOM is ready
      this.modalTarget.addEventListener('shown.bs.modal', () => {
        if (!this.ownerChoices) {
          this.initializeChoices()
        }
      })
    }
  }

  disconnect() {
    if (this.ownerChoices) {
      this.ownerChoices.destroy()
    }

    if (this.hasModalTarget) {
      this.modalTarget.removeEventListener('hidden.bs.modal', this.handleModalClose.bind(this))
      this.modalTarget.removeEventListener('shown.bs.modal', this.handleModalOpen.bind(this))
    }
  }

  handleModalOpen() {
    const storageKey = 'table_selection_credential_ids'
    const credentialIds = this.getSelectedCredentialIds(storageKey)
    if (this.hasSelectionInfoTarget) {
      this.selectionInfoTarget.textContent = `${credentialIds.length} credential(s) selected for reassignment`
    }
  }

  handleModalClose() {
    if (this.ownerChoices) {
      this.ownerChoices.clearStore()
    }

    this.ownerTypeSelectTarget.value = ''
    if (this.hasOwnerSelectContainerTarget) {
      this.ownerSelectContainerTarget.style.display = 'none'
    }
    this.ownersLoaded = false
    this.ownersData = []
  }

  initializeChoices() {
    if (this.hasOwnerSelectTarget) {
      this.ownerChoices = new Choices(this.ownerSelectTarget, {
        searchEnabled: true,
        searchPlaceholderValue: 'Search owners...',
        itemSelectText: '',
        shouldSort: false,
        placeholder: true,
        placeholderValue: 'Select owner...'
      })
    }
  }

  async onOwnerTypeChange(event) {
    const ownerType = event.target.value

    if (!ownerType || ownerType === 'Pool') {
      if (this.hasOwnerSelectContainerTarget) {
        this.ownerSelectContainerTarget.style.display = 'none'
      }
      if (this.hasOwnerSelectTarget) {
        this.ownerSelectTarget.removeAttribute('required')
      }
      return
    }

    if (this.hasOwnerSelectContainerTarget) {
      this.ownerSelectContainerTarget.style.display = 'block'
    }

    if (this.hasOwnerSelectTarget) {
      this.ownerSelectTarget.setAttribute('required', 'required')
    }

    try {
      showLoader()
      await this.loadOwners(ownerType)
    } catch (error) {
      alert('Failed to load owners. Please try again.')
    } finally {
      hideLoader()
    }
  }

  async loadOwners(ownerType) {
    if (this.ownersLoading) {
      return
    }

    this.ownersLoading = true

    try {
      let url
      switch (ownerType) {
        case 'User':
          url = '/admin/users/autocomplete.json'
          break
        case 'Group':
          url = '/admin/groups/autocomplete.json'
          break
        case 'Organization':
          url = '/admin/organizations/autocomplete.json'
          break
        default:
          return
      }

      const response = await fetch(url, {
        headers: {
          'Accept': 'application/json',
          'X-Requested-With': 'XMLHttpRequest'
        }
      })

      if (!response.ok) {
        throw new Error(`Failed to load ${ownerType.toLowerCase()}s: ${response.status}`)
      }

      const owners = await response.json()
      this.populateOwners(owners, ownerType)
      this.ownersLoaded = true
    } catch (error) {
      throw error
    } finally {
      this.ownersLoading = false
    }
  }

  populateOwners(owners, ownerType) {
    this.ownersData = owners

    this.ownerChoices.clearStore()

    const choices = owners.map(owner => {
      let label
      if (ownerType === 'User') {
        label = owner.email || owner.name
      } else {
        label = owner.name
      }

      return {
        value: owner.id.toString(),
        label: label,
        selected: false
      }
    })

    this.ownerChoices.setChoices(choices, 'value', 'label', true)
  }

  async submit(event) {
    event.preventDefault()

    const ownerType = this.ownerTypeSelectTarget.value
    const ownerId = this.ownerSelectTarget.value

    if (!ownerType) {
      alert('Please select an owner type')
      return
    }

    if (ownerType !== 'Pool' && !ownerId) {
      alert('Please select an owner')
      return
    }

    const storageKey = 'table_selection_credential_ids'
    const credentialIds = this.getSelectedCredentialIds(storageKey)

    if (credentialIds.length === 0) {
      alert('No credentials selected')
      return
    }

    const ownerTypeName = ownerType === 'Pool' ? 'Pool (Shared)' : ownerType
    const confirmMessage = `Reassign ${credentialIds.length} credential(s) to ${ownerTypeName}?`

    if (!confirm(confirmMessage)) {
      return
    }

    showLoader()

    const formData = new FormData()
    formData.append('owner_type', ownerType)

    if (ownerType !== 'Pool' && ownerId) {
      formData.append('owner_id', ownerId)
    }

    credentialIds.forEach(credentialId => {
      formData.append('credential_ids[]', credentialId)
    })

    const csrfToken = document.querySelector('meta[name="csrf-token"]')?.content
    const headers = csrfToken ? { "X-CSRF-Token": csrfToken } : {}

    try {
      const response = await fetch(this.formTarget.action, {
        method: 'POST',
        headers,
        body: formData
      })

      const successful = response.ok || (response.status >= 200 && response.status < 400) || response.redirected

      if (!successful) {
        const text = await response.text()
        throw new Error(text || `Request failed with status ${response.status}`)
      }

      this.clearSelection(storageKey)

      const targetUrl = response.redirected && response.url ? response.url : window.location.href
      window.location.assign(targetUrl)
    } catch (error) {
      alert(error.message || 'Failed to reassign credentials')
    } finally {
      hideLoader()
    }
  }

  getSelectedCredentialIds(storageKey) {
    try {
      const stored = sessionStorage.getItem(storageKey)
      const ids = stored ? JSON.parse(stored) : []
      return Array.isArray(ids) ? ids : []
    } catch {
      return []
    }
  }

  clearSelection(storageKey) {
    try {
      sessionStorage.removeItem(storageKey)
    } catch {
      // no-op
    }
  }
}
